import { CrosshairIcon, LogInIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { intentRow } from './AmendRows';
import {
  buildNodeRow,
  buildStepRows,
  buildUnderstandRows,
  continuesInsideNode,
  startingNodeStepOf,
  useAfterAnswerRows,
} from './buildStepRows';
import { DetailRows, DetailRowSpec, PlainRow } from './DetailsRow';
import { getIntentInfo } from './IntentDetail';
import { DialogNodeLink, EntitiesHead } from './StepRows';
import { buildToolRows } from './ToolsRows';
import { ConversationTurn } from '../../../types/conversation';

type WorkflowDetailsProps = { turn: ConversationTurn; grouped?: boolean };

/** Workflow variant: what the user asked → intent → (starting) node → steps → the answer. */
export const WorkflowDetails = ({ turn, grouped }: WorkflowDetailsProps) => {
  const { t } = useTranslation();

  const intentInfo = getIntentInfo(turn);
  const answerRows = useAfterAnswerRows(turn);
  const triggeredNode = turn.metadata.triggeredNode;
  const startingNodeStep = startingNodeStepOf(turn);
  const startingNodeName =
    turn.metadata.nodesStack?.find((node) => node.node_id === startingNodeStep?.node_id)?.name ??
    triggeredNode?.name;
  const rows: DetailRowSpec[] = [];

  rows.push(...buildUnderstandRows(t, turn));

  const entities = turn.request?.entities ?? [];
  if (entities.length > 0) {
    rows.push({
      key: 'entities',
      phase: 'understand',
      icon: <CrosshairIcon className="h-4 w-4" />,
      head: <EntitiesHead entities={entities} />,
    });
  }

  if (intentInfo.hasInfo && !continuesInsideNode(turn)) {
    rows.push({ ...intentRow(turn), phase: 'understand' });
  }

  // Conversation start node (MOV-16477): the session was force-started at a node.
  if (startingNodeStep) {
    rows.push({
      key: 'starting-node',
      icon: <LogInIcon className="h-4 w-4" />,
      head: (
        <PlainRow>
          {t('try_it.details.conversation_starts_at')}{' '}
          <DialogNodeLink nodeId={startingNodeStep.node_id}>{startingNodeName}</DialogNodeLink>
        </PlainRow>
      ),
    });
  }

  const nodeRow = buildNodeRow(t, turn);
  if (nodeRow) {
    rows.push(nodeRow);
  }

  rows.push(...buildToolRows(turn, 'agent'));

  rows.push(...buildStepRows(t, turn, answerRows));

  return <DetailRows rows={rows} grouped={grouped} />;
};
